import { useMemo } from "react";

import { useInvitationsInfiniteQuery } from "./useInvitationsInfiniteQuery";

interface UseAlreadyInvitedDatesOptions {
  email?: string | null;
  forUser?: string;
}

interface AlreadyInvitedInvitation {
  email?: string | null;
  anonymized?: boolean;
  status?: string;
  eventMeetingStartDate?: string | null;
}

/**
 * Sélectionne les dates ISO des réunions où une invitation bloquante existe déjà
 * (non annulée, non anonymisée). Fonction pure, extraite du hook pour être testable.
 *
 * - Flux "par email" (`email`) : tant qu'aucune adresse n'est saisie, aucune date
 *   n'est bloquée (sinon toutes les invitations du club seraient grisées).
 * - Flux "membre" (`forUser`) : la requête est déjà restreinte à l'utilisateur
 *   courant, donc on retourne toutes ses dates.
 */
export function selectAlreadyInvitedDates(
  invitations: AlreadyInvitedInvitation[],
  { email, forUser }: UseAlreadyInvitedDatesOptions,
): string[] {
  if (!forUser && !email) return [];

  return invitations
    .filter(
      (item) =>
        (email ? item.email === email : true) &&
        !item.anonymized &&
        item.status !== "aborted",
    )
    .map((item) => item.eventMeetingStartDate)
    .filter((date): date is string => Boolean(date));
}

/**
 * Dates ISO des réunions où la personne a déjà une invitation que le back refuserait
 * (non annulée, non anonymisée). Réutilise le cache de la liste des invitations du club.
 *
 * - `email` : filtre sur cette adresse (flux où la personne est connue ou saisie).
 * - `forUser: "only"` : invitations de l'utilisateur courant (flux membre).
 */
export function useAlreadyInvitedDates(
  clubSlug: string,
  { email, forUser }: UseAlreadyInvitedDatesOptions = {},
): string[] {
  const { data } = useInvitationsInfiniteQuery({ slug: clubSlug, forUser });

  return useMemo(() => {
    const all = data?.pages.flatMap((page) => page.invitations) ?? [];
    return selectAlreadyInvitedDates(all, { email, forUser });
  }, [data, email, forUser]);
}
